import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  deepCopy,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, StudentCustomIdsPaster, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  Student,
  StudentBulkAssignGroup,
  StudentBulkAssignGroupResource,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { StudentGroupInfo } from "./StudentGroupInfo";
import { SelectedStudents } from "./SelectedStudents";

const routeName = "studentBulkAssignGroup";

export const studentBulkAssignGroupPageRoute: PageRoute = {
  handle: "Students",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentBulkAssignGroupPageState = {
  selectedIds: number[];
};

export const StudentBulkAssignGroupPage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentBulkAssignGroupPageState>(state);
  const [bulkAssignGroup, setBulkAssignGroup] = useState<StudentBulkAssignGroup>({} as StudentBulkAssignGroup);
  const [resource, setResource] = useState<StudentBulkAssignGroupResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkAssignGroup] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkAssignGroup(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkAssignGroup = (ids: number[]) => {
    ApiService.students.getBulkAssignGroup(ids).then((response) => {
      setBulkAssignGroup(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.students.getBulkAssignGroupResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkAssignGroup: StudentBulkAssignGroup) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    let savingBulkAssignGroup = deepCopy(bulkAssignGroup);
    if (savingBulkAssignGroup && savingBulkAssignGroup.isCreateNewGroup === false)
      savingBulkAssignGroup.createStudentGroup = undefined;

    ApiService.students
      .saveBulkAssignGroup(savingBulkAssignGroup)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkAssignGroup]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentBulkAssignGroupPageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton onClick={() => save(bulkAssignGroup)} disabled={!unsavedChanges}/>
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <StudentGroupInfo
          bulkAssignGroup={bulkAssignGroup}
          setBulkAssignGroup={setBulkAssignGroup}
          resource={resource}
          validationErrors={validationErrors}
        />

        <SelectedStudents
          students={new Map(bulkAssignGroup.students?.map((x) => [x.id, x]))}
          setStudents={(value) => setBulkAssignGroup({ ...bulkAssignGroup, students: Array.from(value.values()) })}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
